/**
 * Task goals (docs/plans/boards.md §6, B6 — Lj, Oct 11: "from a task, set the task as a goal, then use subtasks on a
 * kanban board to roll out that feature; set a score for it and show it on the dashboard based on its period; set the
 * goal off hours worked, or subtasks completed, or progress, or review time"). A task goal is a department goal like
 * any other — kept in `opsDepartments/{id}.goals`, counted in the department score — whose subject is one task:
 *
 *   { id, key: 'task:<taskId>', measure, target, period: 'week' | 'month' | 'until', start?, due?, direction?, taskTitle? }
 *
 * Measures (each says what it counted and what it left out, like Field's presets):
 *   hours      hours on the task and every task under it, in the period (target h; a limit when direction = down)
 *   subtasks   its subtasks finished ÷ all of them (until a date: overall; a week / month: the share of what was still
 *              open at the start that got finished in it) — target %
 *   progress   task.progress when the task carries one, else % complete by estimate over the tasks under it, by count
 *              when none have an estimate; a week / month counts the gain in it — target %
 *   review     hours from hand-back to approval, averaged over the task and its subtasks approved in the period
 *              (task.reviewLog; a send-back restarts the clock) — a limit, target h
 *   doneBy     finished on or before the due date = 100%; until then it paces on its progress
 *
 * Period `until` is new: start (the day it was made) → due, pace linear to the date. The goal closes itself when the
 * task is finished or removed, or the date passes: it leaves the score and stays on the department as history (never
 * deleted). Pure — no I/O; deptgoals.js calls in here (this file never imports it).
 */
import { localDateStr } from './time.js';
import { addDaysStr } from './phonestats.js';
import { estimateProgress } from './profit.js';
import { workDepartments } from './workdept.js';
import { listDepartments } from './hierarchy.js';

const H = 3600000;
const DAY = 86400000;
const num = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const round1 = (v) => Math.round(v * 10) / 10;
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const ISO = /^\d{4}-\d{2}-\d{2}$/;

export const TASK_GOAL_MEASURES = Object.freeze(['hours', 'subtasks', 'progress', 'review', 'doneBy']);
export const TASK_GOAL_PERIODS = Object.freeze(['week', 'month', 'until']);

/** What each measure counts (the builder's cards, the tile's unit). */
export const TASK_MEASURE = Object.freeze({
  hours: { label: 'Hours worked', unit: 'h', kind: 'count', direction: 'up', icon: 'clock', hint: 'hours on the task and every subtask under it, from the clock', short: 'on it and its subtasks, from the clock' },
  subtasks: { label: 'Subtasks done', unit: '%', kind: 'count', direction: 'up', icon: 'check-square', hint: 'its subtasks finished ÷ all of them', short: 'finished ÷ all of them' },
  progress: { label: 'Progress', unit: '%', kind: 'count', direction: 'up', icon: 'trending-up', hint: '% complete by estimate over the tasks under it — by count when none have an estimate', short: '% complete by estimate over its subtasks' },
  review: { label: 'Review time', unit: 'h', kind: 'rate', direction: 'down', icon: 'eye', hint: 'hours from hand-back to approval, on it and its subtasks — a limit', short: 'hand-back → approved, a limit' },
  doneBy: { label: 'Done by a date', unit: '%', kind: 'count', direction: 'up', icon: 'flag', hint: 'finished on or before the date = 100%; until then it paces on its progress', short: 'finished by the date = 100%' },
});

export const isTaskGoal = (g) => /^task:./.test(String(g?.key || ''));
export const taskGoalTaskId = (g) => (isTaskGoal(g) ? String(g.key).slice(5) : null);
export const taskGoalMeasure = (g) => (TASK_MEASURE[g?.measure] ? g.measure : 'subtasks');
export const taskGoalKey = (taskId) => `task:${taskId}`;

/** A title for a ring tile: the task's, cut to `max` with an ellipsis. */
export const goalShortTitle = (s, max = 26) => { const t = String(s || '').trim(); return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t; };

/** "Fri 17 Oct" */
export function dueDateText(date) {
  if (!ISO.test(String(date || ''))) return '';
  const d = new Date(`${date}T12:00:00`);
  return `${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()]} ${d.getDate()} ${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()]}`;
}

// ── the tree ────────────────────────────────────────────────────────────────────────────────────────────────────

const live = (t) => t && !t.deletedAt;
const byIdOf = (tasks) => new Map((tasks || []).map((t) => [t.id, t]));
/** Every live task under `id` (children, their children…). */
export function taskSubtree(tasks = [], id) {
  const kids = new Map();
  for (const t of tasks) if (live(t) && t.parentId) { if (!kids.has(t.parentId)) kids.set(t.parentId, []); kids.get(t.parentId).push(t); }
  const out = []; const seen = new Set([id]);
  const walk = (pid) => (kids.get(pid) || []).forEach((k) => { if (!seen.has(k.id)) { seen.add(k.id); out.push(k); walk(k.id); } });
  walk(id);
  return out;
}
const childrenOf = (tasks, id) => (tasks || []).filter((t) => live(t) && t.parentId === id)
  .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || (num(a.createdAt) || 0) - (num(b.createdAt) || 0));
const doneOn = (t) => (t?.status === 'done' && t.completedAt ? localDateStr(t.completedAt) : null);
const doneBy = (t, date) => { const d = doneOn(t); return !!d && d <= date; };
const madeBy = (t, date) => !num(t.createdAt) || localDateStr(num(t.createdAt)) <= date;

// ── the window ──────────────────────────────────────────────────────────────────────────────────────────────────

const WORK_START = 6; const WORK_END = 20;
const dayShare = (now) => { const d = new Date(now); const h = d.getHours() + d.getMinutes() / 60; return Math.max(0, Math.min(1, (h - WORK_START) / (WORK_END - WORK_START))); };
const daysBetween = (a, b) => Math.round((new Date(`${b}T12:00:00`) - new Date(`${a}T12:00:00`)) / DAY);

/**
 * An "until <date>" goal's window: start → due (inclusive), `to` = today (or due once it's passed), share = the days
 * gone ÷ all of them (today counts its share of 6 am → 8 pm) — pace is linear to the date. → { period, from, to, end, share, off }
 */
export function untilWindow(goal, now = Date.now()) {
  const today = localDateStr(now);
  const due = ISO.test(String(goal?.due || '')) ? goal.due : today;
  const start = ISO.test(String(goal?.start || '')) && goal.start <= due ? goal.start : (today <= due ? today : due);
  const total = daysBetween(start, due) + 1;
  let gone;
  if (today > due) gone = total;
  else if (today < start) gone = 0;
  else gone = daysBetween(start, today) + dayShare(now);
  return { period: 'until', from: start, to: today > due ? due : today < start ? start : today, end: due, share: Math.max(0, Math.min(1, gone / total)), off: false };
}

// ── the metric ──────────────────────────────────────────────────────────────────────────────────────────────────

/** The metric a task goal reads as: its measure's unit / kind / direction, labelled with the task's title (cut). */
export function taskGoalMetric(goal, { tasks = [] } = {}) {
  const m = TASK_MEASURE[taskGoalMeasure(goal)];
  const task = byIdOf(tasks).get(taskGoalTaskId(goal));
  const title = task?.title || goal?.taskTitle || 'A task';
  return { ...m, label: goalShortTitle(title), measureLabel: m.label, taskTitle: title, hint: `${m.label.toLowerCase()} — ${m.hint}` };
}

/** The fields a task goal keeps (cleanGoal hands them over): measure, until-period dates, the title for history. */
export function cleanTaskGoalFields(g, out) {
  out.measure = taskGoalMeasure(g);
  if (out.measure === 'doneBy') { out.period = 'until'; out.target = 100; }
  else if (TASK_GOAL_PERIODS.includes(g.period)) out.period = g.period;
  else out.period = 'week';
  if (out.period === 'until') {
    const today = localDateStr(Date.now());
    out.due = ISO.test(String(g.due || '')) ? g.due : addDaysStr(today, 7);
    out.start = ISO.test(String(g.start || '')) && g.start <= out.due ? g.start : (today <= out.due ? today : out.due);
  }
  if (out.measure === 'review') out.direction = 'down';
  else if (out.measure !== 'hours') delete out.direction;   // only hours can be a limit ("stay inside N h")
  delete out.days; delete out.when;   // a task goal counts every day of its period
  if (g.taskTitle) out.taskTitle = String(g.taskTitle).slice(0, 80);
  if (num(g.closedAt)) out.closedAt = num(g.closedAt);
  out.taskId = taskGoalTaskId(g);
  return out;
}

// ── counting ────────────────────────────────────────────────────────────────────────────────────────────────────

/** The task's progress (0–100) as it stood at the end of `date`, and how it was read. */
export function progressAsOf(task, tasks = [], date) {
  if (num(task?.progress) != null) return { pct: Math.max(0, Math.min(100, num(task.progress))), how: 'the task’s own progress', notes: [] };
  const under = taskSubtree(tasks, task.id).filter((t) => madeBy(t, date) && !t.bucket);
  if (!under.length) return { pct: doneBy(task, date) ? 100 : 0, how: 'no subtasks — 100% once it’s done', notes: ['no subtasks — add them to see progress move'] };
  if (doneBy(task, date)) return { pct: 100, how: 'the task is done', notes: [] };
  const est = estimateProgress(under, (t) => doneBy(t, date));
  if (est.pct != null) {
    const leaf = new Set(under.filter((t) => t.parentId).map((t) => t.parentId));
    const noEst = under.filter((t) => !leaf.has(t.id) && !(num(t.estimatedMinutes) > 0)).length;
    return { pct: Math.round(est.pct * 100), how: `by estimate: ${round1(est.doneMin / 60)} of ${round1(est.totalMin / 60)} h finished`, notes: noEst ? [`${plural(noEst, 'subtask')} with no estimate left out — give ${noEst === 1 ? 'it' : 'them'} one to count`] : [] };
  }
  const d = under.filter((t) => doneBy(t, date)).length;
  return { pct: Math.round((d / under.length) * 100), how: `by count: ${d} of ${under.length} subtasks (none have an estimate)`, notes: ['no subtask has an estimate — progress counts them by number'] };
}

/** Every review round on the task and its subtasks: [{ taskId, title, at, decided, decidedAt }] (reviewLog, else the review). */
export function reviewRounds(task, tasks = []) {
  const out = [];
  for (const t of [task, ...taskSubtree(tasks, task.id)]) {
    const log = Array.isArray(t.reviewLog) && t.reviewLog.length ? t.reviewLog : t.review?.at ? [t.review] : [];
    for (const r of log) if (num(r?.at)) out.push({ taskId: t.id, title: t.title, at: num(r.at), decided: r.decided || null, decidedAt: num(r.decidedAt) });
  }
  return out.sort((a, b) => a.at - b.at);
}

/**
 * A task goal's value for a window → { value, rows, basis, notes } (value null = nothing to measure yet).
 * data: { tasks, now }.
 */
export function taskGoalValue(goal, w, data = {}) {
  const tasks = data.tasks || [];
  const task = byIdOf(tasks).get(taskGoalTaskId(goal));
  const measure = taskGoalMeasure(goal);
  if (!task) return { value: null, rows: [], basis: 'the task was removed', notes: ['the task was removed — the goal is closed'] };
  const inWin = (date) => !!date && date >= w.from && date <= w.to;

  if (measure === 'hours') {
    const rows = []; let ms = 0; let undated = 0;
    for (const t of [task, ...taskSubtree(tasks, task.id)]) {
      let mine = 0;
      for (const e of t.timeEntries || []) {
        const d = num(e?.duration) || 0;
        if (d <= 0) continue;
        if (!e.date) { undated += 1; continue; }
        if (inWin(e.date)) mine += d;
      }
      if (mine > 0) { ms += mine; rows.push({ taskId: t.id, title: `${t.id === task.id ? t.title : `↳ ${t.title}`} · ${round1(mine / H)} h`, ms: mine, hours: round1(mine / H) }); }
    }
    const kids = taskSubtree(tasks, task.id).length;
    return { value: round1(ms / H), rows: rows.sort((a, b) => b.ms - a.ms), basis: `${round1(ms / H)} h on ${task.title}${kids ? ` and its ${plural(kids, 'subtask')}` : ''}, ${w.period === 'until' ? 'since it became a goal' : `this ${w.period}`}`, notes: undated ? [`${plural(undated, 'time entry', 'time entries')} with no date left out`] : [] };
  }

  if (measure === 'subtasks') {
    const kids = childrenOf(tasks, task.id).filter((t) => madeBy(t, w.to));
    if (!kids.length) return { value: null, rows: [], basis: 'no subtasks yet', notes: ['no subtasks yet — add them (or “Roll it out as a feature”) to count'] };
    const rows = kids.map((t) => { const done = doneBy(t, w.to); return { taskId: t.id, title: done ? t.title : `${t.title} · open`, date: done ? doneOn(t) : null, done }; });
    if (w.period === 'until') {
      const d = rows.filter((r) => r.done).length;
      return { value: Math.round((d / kids.length) * 100), rows, basis: `${d} of ${plural(kids.length, 'subtask')} done`, notes: [], count: { done: d, total: kids.length } };
    }
    const before = kids.filter((t) => { const d = doneOn(t); return d && d < w.from; }).length;
    const pool = kids.length - before;
    const d = rows.filter((r) => r.done && r.date >= w.from).length;
    if (!pool) return { value: 100, rows, basis: `all ${plural(kids.length, 'subtask')} were done before this ${w.period}`, notes: [], count: { done: 0, total: 0 } };
    return { value: Math.round((d / pool) * 100), rows: rows.filter((r) => !r.date || r.date >= w.from), basis: `${d} of the ${plural(pool, 'subtask')} open at the start of the ${w.period} done`, notes: before ? [`${before} done before this ${w.period} not counted`] : [], count: { done: d, total: pool } };
  }

  if (measure === 'progress' || measure === 'doneBy') {
    const p = progressAsOf(task, tasks, w.to);
    if (measure === 'doneBy') {
      const d = doneOn(task);
      if (d && d <= w.to) return { value: 100, rows: [], basis: `finished ${dueDateText(d)}${goal.due ? `, due ${dueDateText(goal.due)}` : ''}`, notes: [] };
      return { value: p.pct, rows: [], basis: `not finished yet — ${p.pct}% (${p.how}) against the pace to ${dueDateText(goal.due || w.end)}`, notes: p.notes };
    }
    if (w.period === 'until') return { value: p.pct, rows: [], basis: `${p.pct}% — ${p.how}`, notes: p.notes };
    const start = progressAsOf(task, tasks, addDaysStr(w.from, -1));
    const gain = Math.max(0, p.pct - start.pct);
    return { value: gain, rows: [], basis: `${start.pct}% → ${p.pct}% this ${w.period} (${p.how})`, notes: p.notes };
  }

  // review: hours from hand-back to approval, decided in the window
  const rounds = reviewRounds(task, tasks);
  const approved = rounds.filter((r) => r.decided === 'approved' && r.decidedAt && inWin(localDateStr(r.decidedAt)));
  const sent = rounds.filter((r) => r.decided === 'sent' && r.decidedAt && inWin(localDateStr(r.decidedAt))).length;
  const now = data.now || Date.now();
  const waiting = rounds.filter((r) => !r.decided);
  const notes = [];
  if (sent) notes.push(`${sent} sent back — counted from ${sent === 1 ? 'its' : 'their'} last hand-back`);
  if (waiting.length) notes.push(`${plural(waiting.length, 'task')} waiting for review (${round1((now - waiting[0].at) / H)} h so far) — counted once approved`);
  if (!approved.length) return { value: null, rows: [], basis: 'nothing approved in this period yet', notes };
  const rows = approved.map((r) => ({ taskId: r.taskId, title: r.title, hours: round1((r.decidedAt - r.at) / H), date: localDateStr(r.decidedAt) }));
  const avg = round1(rows.reduce((a, r) => a + r.hours, 0) / rows.length);
  return { value: avg, rows, basis: `average of ${plural(rows.length, 'approval')}: hand-back → approved`, notes };
}

// ── closing ─────────────────────────────────────────────────────────────────────────────────────────────────────

/**
 * Is the goal over (as of `now`)? Its task finished or removed, its date passed, or closed by hand.
 * → null | { why: 'done' | 'removed' | 'ended' | 'closed', at (ms | null) }
 */
export function taskGoalClosed(goal, data = {}, now = data.now || Date.now()) {
  if (!isTaskGoal(goal)) return null;
  if (num(goal.closedAt) && num(goal.closedAt) <= now) return { why: 'closed', at: num(goal.closedAt) };
  const task = byIdOf(data.tasks || []).get(taskGoalTaskId(goal));
  if (!task || task.deletedAt) return { why: 'removed', at: num(task?.deletedAt) };
  if (task.status === 'done' && num(task.completedAt) && num(task.completedAt) <= now) return { why: 'done', at: num(task.completedAt) };
  if (goal.period === 'until' && ISO.test(String(goal.due || '')) && localDateStr(now) > goal.due) return { why: 'ended', at: new Date(`${goal.due}T23:59:00`).getTime() };
  return null;
}
export const CLOSED_WORD = Object.freeze({ done: 'DONE', removed: 'TASK REMOVED', ended: 'DATE PASSED', closed: 'CLOSED' });

// ── the builder ─────────────────────────────────────────────────────────────────────────────────────────────────

/** Where a task's goal lives: the task's department (its own, else its project's, else General). → dept */
export function taskGoalDepartment(task, data = {}) {
  const { tasks = [], projects = [], departments = [], departmentMode = true } = data;
  const wd = workDepartments({ tasks, projects, departments, departmentMode });
  const d = task ? wd.forTask(task) : null;
  const list = listDepartments(departments);
  return list.find((x) => x.id === d?.id) || list.find((x) => x.id === 'general') || null;
}

/** The goal on a task, wherever it's kept (open first, then the newest closed). → { dept, goal } | null */
export function taskGoalLookup(taskId, departments = []) {
  const hits = [];
  for (const d of listDepartments(departments)) for (const g of Array.isArray(d.goals) ? d.goals : []) if (g && g.key === taskGoalKey(taskId)) hits.push({ dept: d, goal: g });
  if (!hits.length) return null;
  return hits.find((h) => !num(h.goal.closedAt)) || hits[hits.length - 1];
}

/** What each measure reads today, for the builder's cards: "3 of 7 subtasks done" · "9.5 h so far (14 h est)". */
export function taskMeasureToday(measure, task, data = {}) {
  if (!task) return '';
  const tasks = data.tasks || [];
  const today = localDateStr(data.now || Date.now());
  const under = taskSubtree(tasks, task.id);
  if (measure === 'hours') {
    const ms = [task, ...under].reduce((a, t) => a + (t.timeEntries || []).reduce((b, e) => b + (num(e?.duration) || 0), 0), 0);
    const est = [task, ...under].filter((t) => !under.some((u) => u.parentId === t.id)).reduce((a, t) => a + (num(t.estimatedMinutes) || 0), 0);
    return `${round1(ms / H)} h so far${est ? ` · ${round1(est / 60)} h estimated` : ''}`;
  }
  if (measure === 'subtasks') {
    const kids = childrenOf(tasks, task.id);
    return kids.length ? `${kids.filter((t) => t.status === 'done').length} of ${plural(kids.length, 'subtask')} done` : 'no subtasks yet';
  }
  if (measure === 'progress') { const p = progressAsOf(task, tasks, today); return taskSubtree(tasks, task.id).length ? `${p.pct}% complete` : 'no subtasks yet'; }
  if (measure === 'review') {
    const r = reviewRounds(task, tasks).filter((x) => x.decided === 'approved' && x.decidedAt);
    if (!r.length) return 'no approvals yet';
    return `avg ${round1(r.reduce((a, x) => a + (x.decidedAt - x.at), 0) / r.length / H)} h to approve · ${plural(r.length, 'review')}`;
  }
  if (measure === 'doneBy') return task.status === 'done' ? 'already done' : `${progressAsOf(task, tasks, today).pct}% there`;
  return '';
}

/**
 * The builder's suggested target for a measure + period → { value, why }:
 *   hours — the estimate left on the tree (until), else the last 4 weeks' average on it (+10%), else 5 h a week;
 *   subtasks / progress — all of it (100%) until a date or over a month, half / a quarter a week;
 *   review — your last 4 weeks' approvals averaged (−10%), else 24 h; doneBy — 100%.
 */
export function taskGoalSuggest(goal, data = {}) {
  const tasks = data.tasks || [];
  const task = byIdOf(tasks).get(taskGoalTaskId(goal));
  const measure = taskGoalMeasure(goal);
  const period = measure === 'doneBy' ? 'until' : goal.period || 'week';
  const now = data.now || Date.now();
  if (measure === 'doneBy') return { value: 100, why: 'finished by the date' };
  if (measure === 'subtasks') {
    const n = task ? childrenOf(tasks, task.id).length : 0;
    return period === 'week' ? { value: 50, why: n ? `half of what’s open each week (${plural(n, 'subtask')})` : 'half each week' } : { value: 100, why: n ? `all ${plural(n, 'subtask')}` : 'all of them' };
  }
  if (measure === 'progress') return period === 'week' ? { value: 25, why: 'a quarter of the way each week' } : { value: 100, why: 'all the way' };
  if (measure === 'hours') {
    if (!task) return { value: null, why: '' };
    const tree = [task, ...taskSubtree(tasks, task.id)];
    const leaves = tree.filter((t) => !tree.some((u) => u.parentId === t.id));
    const estMin = leaves.reduce((a, t) => a + (num(t.estimatedMinutes) || 0), 0);
    const spent = tree.reduce((a, t) => a + (t.timeEntries || []).reduce((b, e) => b + (num(e?.duration) || 0), 0), 0);
    if (period === 'until') return estMin ? { value: Math.max(1, Math.round(Math.max(0, estMin * 60000 - spent) / H)), why: `the estimate left: ${round1(estMin / 60)} h estimated, ${round1(spent / H)} h in` } : { value: null, why: '' };
    const today = localDateStr(now);
    const from = addDaysStr(today, -28);
    const recent = tree.reduce((a, t) => a + (t.timeEntries || []).filter((e) => e.date && e.date >= from && e.date < today).reduce((b, e) => b + (num(e?.duration) || 0), 0), 0);
    const per = period === 'month' ? 4.345 : 1;
    if (recent > 0) { const v = Math.max(1, Math.round((recent / H / 4) * per * 1.1)); return { value: v, why: `your last 4 weeks on it: ${round1(recent / H)} h → +10%` }; }
    return { value: period === 'month' ? 20 : 5, why: 'a start — change it any time' };
  }
  // review
  const from = now - 28 * DAY;
  const all = tasks.flatMap((t) => (Array.isArray(t.reviewLog) ? t.reviewLog : [])).filter((r) => r?.decided === 'approved' && num(r.decidedAt) >= from && num(r.at));
  if (all.length) { const avg = all.reduce((a, r) => a + (r.decidedAt - r.at), 0) / all.length / H; return { value: Math.max(1, Math.round(avg * 0.9)), why: `your last 4 weeks: ${plural(all.length, 'approval')}, ${round1(avg)} h on average → −10%` }; }
  return { value: 24, why: 'a day — change it any time' };
}

/** The period in words: "a week" · "a month" · "by Fri 17 Oct". */
export const taskPeriodText = (goal) => (goal.period === 'until' || goal.measure === 'doneBy' ? `by ${dueDateText(goal.due)}` : goal.period === 'month' ? 'a month' : 'a week');

/** The review sentence: "Boards rollout: all 11 subtasks done by Fri 17 Oct, in the Dev score." */
export function taskGoalSentence(goal, data = {}, dept = null) {
  const tasks = data.tasks || [];
  const task = byIdOf(tasks).get(taskGoalTaskId(goal));
  const title = task?.title || goal.taskTitle || 'This task';
  const t = num(goal.target);
  const when = taskPeriodText(goal);
  const where = dept?.name ? `, in the ${dept.name} score` : '';
  const m = taskGoalMeasure(goal);
  if (m === 'doneBy') return `${title}: finished ${when}${where}.`;
  if (m === 'hours') return `${title}: ${goal.direction === 'down' ? 'at most' : 'at least'} ${t ?? '—'} h of work ${when}, its subtasks included${where}.`;
  if (m === 'subtasks') {
    const n = task ? childrenOf(tasks, task.id).length : 0;
    return `${title}: ${t === 100 && goal.period === 'until' && n ? `all ${plural(n, 'subtask')}` : `${t ?? '—'}% of its subtasks`} done ${when}${where}.`;
  }
  if (m === 'progress') return `${title}: ${goal.period === 'until' ? `${t ?? '—'}% complete ${when}` : `${t ?? '—'}% more progress ${when}`}${where}.`;
  return `${title}: work handed back is approved within ${t ?? '—'} h on average (${goal.period === 'until' ? `until ${dueDateText(goal.due)}` : `each ${goal.period || 'week'}`})${where}.`;
}

/** Quick due dates for the builder: this Friday, next Friday, two weeks, the month's end. → [{ label, date }] */
export function dueChoices(now = Date.now()) {
  const today = localDateStr(now);
  const dow = new Date(`${today}T12:00:00`).getDay();
  const fri = addDaysStr(today, ((5 - dow + 7) % 7) || 7);
  const d = new Date(`${today}T12:00:00`);
  const eom = localDateStr(new Date(d.getFullYear(), d.getMonth() + 1, 0, 12).getTime());
  const out = [
    { label: `This Friday · ${dueDateText(fri)}`, date: fri },
    { label: `Next Friday · ${dueDateText(addDaysStr(fri, 7))}`, date: addDaysStr(fri, 7) },
    { label: `In two weeks · ${dueDateText(addDaysStr(today, 14))}`, date: addDaysStr(today, 14) },
    { label: `End of the month · ${dueDateText(eom)}`, date: eom },
  ];
  const seen = new Set();
  return out.filter((x) => x.date > today && !seen.has(x.date) && seen.add(x.date));
}

/** "Roll it out as a feature" — the standard subtasks a task with none can start from (boards.md §6.4). */
export const FEATURE_ROLLOUT_STEPS = Object.freeze(['Plan', 'Build', 'Test', 'Deploy', 'Check it live']);

// ── what to work on (scoreinsights' moves) ──────────────────────────────────────────────────────────────────────

const simDone = (t, now) => ({ ...t, status: 'done', completedAt: now });
const simHours = (t, ms, now) => ({ ...t, timeSpent: (num(t.timeSpent) || 0) + ms, timeEntries: [...(t.timeEntries || []), { id: `sim-tg-${t.id}`, date: localDateStr(now), duration: ms, createdAt: now }] });

/**
 * The moves a task goal behind pace offers, for scoreMoves to re-score: work N h on the task, finish N subtasks.
 * g = the goal's row in the view. → [{ move: { id, kind, title, why, action }, changed: [tasks] }]
 */
export function taskGoalMoves(g, data = {}, now = data.now || Date.now()) {
  if (!isTaskGoal(g) || g.mark == null || g.mark >= 100 || g.target == null) return [];
  const tasks = data.tasks || [];
  const task = byIdOf(tasks).get(taskGoalTaskId(g));
  if (!task || task.status === 'done') return [];
  const m = taskGoalMeasure(g);
  const short = g.expected != null && g.value != null ? g.expected - g.value : null;
  const open = childrenOf(tasks, task.id).filter((t) => t.status !== 'done' && t.status !== 'skipped');
  const title = goalShortTitle(task.title, 40);
  if (m === 'hours' && g.direction !== 'down' && short > 0) {
    const end = new Date(now); end.setHours(20, 0, 0, 0);
    const ms = Math.min(Math.ceil((short * H) / (15 * 60000)) * 15 * 60000, Math.max(15 * 60000, end.getTime() - now), 10 * H);
    const on = open[0] || task;
    const h = round1(ms / H);
    return [{ move: { id: `taskhours:${g.id}`, kind: 'hours', title: `Work ${h} h on ${title}`, why: `${g.measureLabel || 'Hours'} is ${round1(short)} h behind pace${on !== task ? ` — next: ${on.title}` : ''}`, action: { kind: 'start', taskId: on.id } }, changed: [simHours(on, ms, now)] }];
  }
  if ((m === 'subtasks' || m === 'progress' || m === 'doneBy') && open.length && short > 0) {
    let k = 1;
    if (m === 'subtasks' && g.count?.total) k = Math.max(1, Math.ceil((short / 100) * g.count.total - 1e-9));
    else if (m !== 'subtasks') {
      // enough subtasks (in order) to cover the progress short of pace, by estimate when they have one
      const est = open.map((t) => num(t.estimatedMinutes) || 0);
      const all = taskSubtree(tasks, task.id).reduce((a, t) => a + (num(t.estimatedMinutes) || 0), 0);
      if (all > 0) { let got = 0; k = 0; while (k < open.length && (got / all) * 100 < short) { got += est[k]; k += 1; } k = Math.max(1, k); }
      else k = Math.max(1, Math.ceil((short / 100) * childrenOf(tasks, task.id).length - 1e-9));
    }
    k = Math.min(k, open.length);
    const pick = open.slice(0, k);
    return [{ move: { id: `tasksubs:${g.id}`, kind: 'finishN', title: k === 1 ? `Finish ${pick[0].title}` : `Finish ${k} subtasks of ${title}`, why: k === 1 ? `${title} is behind pace` : pick.map((t) => t.title).slice(0, 2).join(', ') + (k > 2 ? ` +${k - 2} more` : ''), action: { kind: 'open', taskId: pick[0].id } }, changed: pick.map((t) => simDone(t, now)) }];
  }
  return [];
}
